import * as go from 'gojs';
import { MetricTypes, ScreenCalcMethods } from 'store/settings/types';
import { StorageUnitStatus } from 'store/storageunits/types';
import SandvikLogo from './sandvik.jpg';
import { checkIfRoleIsIncluded, UserRole } from 'auth/checkRole';
import { getTextStroke } from './baseUnitUtils';

const $ = go.GraphObject.make;

const licenseText =
  'Please note that PlantDesigner® gives indicative values, based on typical operating conditions. Since operating conditions can vary very widely, no warranty of these recommendations, either expressed or implied, is given by PlantDesigner®, its owners or supplier.';

const getFont = () => {
  return new go.Binding('font', 'font', (font) => {
    return `normal normal 500 8px ${font}`;
  });
};

function labelStyle() {
  return [
    {
      alignment: go.Spot.TopLeft,
      margin: 2,
      stroke: getTextStroke(),
      font: 'normal normal 500 5px Sandvik Sans Text',
    },
  ];
}
function fieldStyle(prop: string) {
  return [
    {
      alignment: go.Spot.TopLeft,
      margin: new go.Margin(8, 2, 2, 2),
      stroke: getTextStroke(),
      font: 'normal normal 500 8px Sandvik Sans Text',
    },
    new go.Binding('text', prop).makeTwoWay(),
    getFont(),
  ];
}

export const PrintHead = () => {
  return $(
    go.Panel,
    'Auto',
    $(go.Shape, { fill: 'white', stroke: 'black', strokeWidth: 1 }),
    $(
      go.Panel,
      'Table',
      $(
        go.Panel,

        {
          row: 0,
          columnSpan: 2,
          stretch: go.GraphObject.Fill,
          margin: 0.5,
          background: 'white',
          alignment: go.Spot.Left,
        },
        $(
          go.Panel,
          'Table',
          { stretch: go.GraphObject.Fill, margin: 10 },
          $(
            go.Panel,
            'Viewbox',
            {
              column: 0,
              maxSize: new go.Size(80, 40),
              alignment: go.Spot.Center,
              margin: new go.Margin(0, 0, 0, 0),
            },
            $(go.Picture, {
              source: SandvikLogo,
            }),
          ),
          $(
            go.Panel,
            'Viewbox',
            {
              column: 1,
              maxSize: new go.Size(50, 25),
              alignment: go.Spot.Center,
              margin: new go.Margin(0, 0, 0, 0),
            },
            $(
              go.Picture,
              new go.Binding('source', '', (node) => {
                return checkIfRoleIsIncluded(UserRole.EXTERNAL)
                  ? node.projectData.customLogo
                  : node.projectData.customLogo !== ''
                    ? node.projectData.customLogo
                    : '';
              }),
            ),
          ),
          $(
            go.Panel,
            'Vertical',
            { column: 2, alignment: go.Spot.Center },
            $(go.TextBlock, 'PlantDesigner', {
              font: 'normal normal 500 13px Sandvik Sans Text',
            }),
            $(
              go.TextBlock,
              {
                font: 'normal normal 500 8px Sandvik Sans Text',
              },
              new go.Binding('text', '', (_role) => {
                return checkIfRoleIsIncluded(UserRole.EXTERNAL)
                  ? 'External Version'
                  : 'Flow Sheet Calculations';
              }),
            ),
          ),
          $(
            go.TextBlock,
            {
              column: 3,
              stroke: 'red',
              font: 'normal normal 500 13px Sandvik Sans Text',
              alignment: go.Spot.Center,
            },
            new go.Binding('text', 'fileStatus', (fileStatus) => {
              return !fileStatus.includes(StorageUnitStatus.APPROVED)
                ? 'Preliminary'
                : '';
            }),
          ),
        ),
      ),
      $(go.RowColumnDefinition, {
        row: 0,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
      }),

      $(go.RowColumnDefinition, {
        row: 1,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 19,
      }),

      $(go.RowColumnDefinition, {
        row: 2,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 19,
      }),

      $(go.RowColumnDefinition, {
        row: 3,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 19,
      }),

      $(go.RowColumnDefinition, {
        row: 4,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 19,
      }),

      $(go.RowColumnDefinition, {
        row: 5,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 19,
      }),

      $(go.RowColumnDefinition, {
        row: 6,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 19,
      }),

      $(go.RowColumnDefinition, { column: 0, minimum: 100 }),
      $(go.RowColumnDefinition, {
        column: 1,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
        minimum: 250,
      }),

      // Left column for date, drawn by, revised by and approved by
      $(go.TextBlock, 'Date:', { row: 1, column: 0 }, labelStyle()),
      $(
        go.TextBlock,
        new go.Binding('text', 'projectData', (projectData) => {
          return projectData.currentDate;
        }),
        { row: 1, column: 0 },
        fieldStyle('date'),
      ),
      $(go.TextBlock, 'Drawn by:', { row: 2, column: 0 }, labelStyle()),
      $(
        go.TextBlock,
        new go.Binding('text', 'projectData', (projectData) => {
          return projectData.drawnBy;
        }),
        { row: 2, column: 0 },
        fieldStyle('drawnby'),
      ),
      $(go.TextBlock, 'Revised by:', { row: 3, column: 0 }, labelStyle()),
      $(
        go.TextBlock,
        { row: 3, column: 0 },
        new go.Binding('text', 'projectData', (projectData) => {
          return projectData.revisedBy;
        }),
        fieldStyle('revisedby'),
      ),
      $(
        go.TextBlock,
        'Approved by:',
        { row: 4, column: 0 },
        new go.Binding('visible', '', (_role) => {
          return !checkIfRoleIsIncluded(UserRole.EXTERNAL);
        }),
        labelStyle(),
      ),
      $(
        go.TextBlock,

        { row: 4, column: 0 },
        new go.Binding('text', 'approvedBy'),
        new go.Binding('visible', '', (_role) => {
          return !checkIfRoleIsIncluded(UserRole.EXTERNAL);
        }),
        fieldStyle('appprovedby'),
      ),

      // Table for customer, project, project no and flowsheet no
      $(
        go.TextBlock,
        'Customer:',
        { row: 1, column: 1, columnSpan: 2 },
        labelStyle(),
      ),
      $(
        go.TextBlock,
        { row: 1, column: 1, columnSpan: 2 },
        new go.Binding('text', 'projectData', (projectData) => {
          return projectData.customer;
        }),
        fieldStyle('customer'),
      ),
      $(
        go.Panel,
        {
          row: 2,
          column: 1,
          rowSpan: 2,
          stretch: go.GraphObject.Fill,
          background: 'white',
          margin: 0.5,
        },
        $(go.TextBlock, 'Project:', labelStyle()),
        $(
          go.TextBlock,
          new go.Binding('text', 'projectData', (projectData) => {
            return projectData.projectLineOne;
          }),
          fieldStyle('project'),
        ),
      ),
      $(
        go.Panel,
        {
          row: 4,
          column: 1,
          stretch: go.GraphObject.Fill,
          background: 'transparent',
        },
        $(
          go.Panel,
          'Table',
          { stretch: go.GraphObject.Fill },
          $(go.RowColumnDefinition, {
            column: 1,
            separatorStroke: 'black',
            separatorStrokeWidth: 1,
          }),
          $(go.RowColumnDefinition, { column: 0, minimum: 100 }),
          $(go.TextBlock, 'Project no:', { column: 0 }, labelStyle()),
          $(
            go.TextBlock,
            { column: 0 },
            fieldStyle('projectno'),
            new go.Binding('text', 'projectData', (projectData) => {
              return projectData.projectNo;
            }),
          ),
          $(go.TextBlock, 'Flowsheet no:', { column: 1 }, labelStyle()),
          $(
            go.TextBlock,
            { column: 1 },
            fieldStyle('flowsheetno'),
            new go.Binding('text', 'projectData', (projectData) => {
              return projectData.flowSheetNo;
            }),
          ),
        ),
      ),
      $(go.TextBlock, 'Last sim:', { row: 5, column: 0 }, labelStyle()),
      $(
        go.TextBlock,
        { row: 5, column: 0 },
        new go.Binding('text', 'projectData', (projectData) => {
          return projectData.lastSimulation;
        }),
        fieldStyle('lastSim'),
      ),
      $(
        go.Panel,
        'Auto',
        {
          row: 6,
          columnSpan: 2,
          stretch: go.GraphObject.Fill,
          background: 'white',
          alignment: go.Spot.Left,
        },
        $(go.TextBlock, {
          background: 'transparent',
          stretch: go.GraphObject.Fill,
          text: licenseText,
          font: 'normal normal 500 5px Sandvik Sans Text',
          margin: 2,
        }),
      ),
    ),
  );
};

export const RemarksPrintHead = () => {
  return $(
    go.Panel,
    'Auto',
    {
      alignment: go.Spot.BottomRight,
      alignmentFocus: go.Spot.BottomRight,
    },
    $(go.Shape, { fill: 'white', stroke: 'black', strokeWidth: 1 }),
    $(
      go.Panel,
      'Table',
      $(go.TextBlock, 'Remarks', {
        font: 'normal normal 500 6px Sandvik Sans Text',
        row: 0,
        alignment: go.Spot.Center,
      }),

      $(go.RowColumnDefinition, {
        row: 0,
      }),

      $(go.RowColumnDefinition, {
        row: 1,
        separatorStroke: 'black',
        separatorStrokeWidth: 1,
      }),
      $(go.RowColumnDefinition, { column: 0, minimum: 150 }),

      $(
        go.Panel,
        'Vertical',
        {
          row: 1,
          stretch: go.GraphObject.Fill,
          background: 'transparent',
        },
        $(go.TextBlock, 'Flowsheet calculated with:', labelStyle()),
        $(
          go.TextBlock,
          new go.Binding('text', 'metricType', (metricType) => {
            return metricType.value === MetricTypes.METRIC_SQUARE.value
              ? '- Metric units, Square Laboratory sieves'
              : metricType.value === MetricTypes.METRIC_ROUND.value
                ? '- Metric units, Round Laboratory sieves.'
                : '- Imperial units, Square Laboratory sieves';
          }),
          labelStyle(),
        ),
        $(
          go.TextBlock,
          labelStyle(),
          new go.Binding('text', 'screenCalcMethod', (screenCalcMethod) => {
            return screenCalcMethod === ScreenCalcMethods.NORMAL.name
              ? '- Normal Screen Calculation Method'
              : '- Efficiency Based Screen Calculation Method';
          }),
        ),
      ),
    ),
  );
};

export const CompletePrintHead = (width: number, height: number) => {
  return $(
    go.Panel,
    'Horizontal',
    {
      alignment: go.Spot.BottomRight,
      alignmentFocus: go.Spot.BottomRight,
    },
    new go.Binding('scale', 'desiredSize', function (s, node) {
      const format = node.part.data.format;
      const orientation = node.part.data.orientation;
      switch (orientation) {
        case 'Landscape':
          switch (format) {
            case 'A3':
              return (s.width / width) * 0.4;
            case 'A2':
              return (s.width / width) * 0.3;
            case 'Tabloid':
              return (s.width / width) * 0.4;
            default:
              return (s.width / width) * 0.5;
          }
        case 'Portrait':
          switch (format) {
            case 'A3':
              return (s.height / height) * 0.6;
            case 'A2':
              return (s.height / height) * 0.5;
            case 'Tabloid':
              return (s.height / height) * 0.7;
            default:
              return (s.height / height) * 0.9;
          }
        default:
          return s.width / width;
      }
    }).ofObject('SHAPE'),

    RemarksPrintHead(),
    PrintHead(),
  );
};
